import lod_ from "lodash";
import { Card, CardContent, Icon, IconButton, Menu, MenuItem } from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import DefaultDataTable from "components/Custom/Tables/DefaultDataTable";
import { useEffect, useState } from "react";
import { t } from "i18next";
import { useDispatch, useSelector } from "react-redux";
import FormAction from "redux-react/actions/formAction";
import ConfirmDialogButton from "components/Custom/Dialogs/ConfirmDialogButton";
import { selectKnowledgeBaseConfig } from "redux-react/reducers/knowledgeBaseReducer";

/**
 * Theme Card component
 * Displays a card with theme information and allows editing and deleting themes.
 * @param {Object} props - Component properties
 * @param {Array} props.folders - List of folders
 * @param {Object} props.theme - Theme object to display
 * @param {Function} props.onEdit - Function to call when editing a theme
 * @param {Function} props.handleDeleteFolder - Function to call when deleting a theme
 * @param {Function} props.getFolders - Function to fetch folders (optional)
 * @returns {JSX.Element} Rendered ThemeCard component
 */
const ThemeDisplay = ({ folders, theme, onEdit, handleDeleteFolder }) => {
	const knowledgeBaseConfig = useSelector(selectKnowledgeBaseConfig);

	const dispatch = useDispatch();
	const [themes, setThemes] = useState([]);

	const [menuRef, setMenuRef] = useState(null);
	const [menuValues, setMenuValues] = useState({});

	/**
	 * Fetch categories for the theme
	 */
	const getThemes = async () => {
		dispatch(
			FormAction.getItemsFromCollection(
				"asset",
				{
					noActive: true,
					query: {
						knowledgeBaseCode: knowledgeBaseConfig.code,
						type: "asset",
						format: "theme"
					}
				},
				res => {
					setThemes(res?.items || []);
				}
			)
		);
	};

	useEffect(() => {
		getThemes();
	}, [folders, theme]);

	if (lod_.isNil(theme)) {
		return null;
	}

	return (
		<MDBox
			sx={{
				height: "100%"
			}}
		>
			<Card
				sx={{
					height: "100%"
				}}
			>
				<CardContent
					sx={{
						height: "100%",
						display: "flex",
						flexDirection: "column"
					}}
					style={{
						margin: 0,
						padding: 0
					}}
				>
					<MDBox display="flex" justifyContent="space-between" alignItems="center">
						<MDBox>
							<MDTypography variant="h5" sx={{ ml: 3, mt: 3 }}>
								{theme.name}
							</MDTypography>
							<MDTypography variant="subtitle2" sx={{ ml: 3 }}>
								{t("ASSETS.THEME.allThemes")}
							</MDTypography>
						</MDBox>
					</MDBox>

					<MDBox flex="1">
						<DefaultDataTable
							list={null}
							pagination={{
								defaultEntrie: 50,
								entriesPerPage: [25, 50, 76, 100]
							}}
							noMargin
							noHeader
							filters={{}}
							canSearch
							table={{
								rows: themes,
								columns: [
									{ accessor: "_id", Header: "id" },
									{ accessor: "name", Header: t("SETTINGS.PAGES.name") },
									{ accessor: "description", Header: t("ASSETS.DETAILS.description") },
									{ accessor: "fileType", Header: "fileType" },
									{ accessor: "format", Header: "format" }
								],
								count: themes.length
							}}
							display={["name", "description"]}
							actions={[
								<IconButton
									handleclick={({ values }, e) => {
										setMenuValues(values);
										setMenuRef(e.currentTarget);
										e.stopPropagation();
										e.preventDefault();
									}}
								>
									<Icon fontSize="medium">more_vert</Icon>
								</IconButton>
							]}
							compact
						/>
					</MDBox>
					{/* Menu actions selection */}
					<Menu
						open={Boolean(menuRef)}
						anchorEl={menuRef}
						onClose={() => {
							setMenuRef(null);
						}}
					>
						{/* Options */}
						<MenuItem disabled style={{ opacity: 1 }}>
							<MDTypography variant="caption">{t("SETTINGS.edition")}</MDTypography>
						</MenuItem>
						<MenuItem
							disabled={menuValues.type === "multiCollection"}
							onClick={() => {
								onEdit(menuValues);
								setMenuRef(null);
							}}
						>
							<Icon fontSize="medium">edit</Icon>&nbsp;{t("SETTINGS.edit")}
						</MenuItem>

						<ConfirmDialogButton
							onConfirm={(e, values) => {
								handleDeleteFolder(values);
								setMenuRef(null);
							}}
							component={
								<MenuItem>
									<Icon fontSize="medium">delete</Icon>&nbsp;{t("SETTINGS.delete")}
								</MenuItem>
							}
							rowValues={menuValues}
							displayAttribute="name"
						/>
					</Menu>
				</CardContent>
			</Card>
		</MDBox>
	);
};

export default ThemeDisplay;
